import { YOUTUBE_CHANNELS, YOUTUBE_ENDPOINTS, YOUTUBE_API_CONFIG } from '../config/youtubeChannels';

class YouTubeService {
  constructor() {
    this.apiKey = YOUTUBE_API_CONFIG.API_KEY;
  }

  // Check if API key is configured
  isApiKeyConfigured() {
    return this.apiKey && this.apiKey !== 'YOUR_YOUTUBE_API_KEY_HERE';
  }

  // Fetch live videos from all configured channels
  async fetchLiveVideos() {
    if (!this.isApiKeyConfigured()) {
      console.warn('YouTube API key not configured. Using mock data.');
      return this.getMockLiveVideos();
    }

    try {
      const liveVideos = [];
      
      // Fetch live videos from each channel
      for (const channelId of YOUTUBE_CHANNELS) {
        try {
          const channelLiveVideos = await this.fetchChannelLiveVideos(channelId);
          liveVideos.push(...channelLiveVideos);
        } catch (error) {
          console.error(`Error fetching live videos for channel ${channelId}:`, error);
        }
      }

      return liveVideos;
    } catch (error) {
      console.error('Error fetching live videos:', error);
      return this.getMockLiveVideos();
    }
  }

  // Fetch live videos for a specific channel
  async fetchChannelLiveVideos(channelId) {
    const searchParams = new URLSearchParams({
      part: 'snippet',
      channelId: channelId,
      eventType: 'live',
      type: 'video',
      key: this.apiKey,
      maxResults: 5
    });

    const response = await fetch(`${YOUTUBE_ENDPOINTS.SEARCH_LIVE}?${searchParams}`);
    
    if (!response.ok) {
      throw new Error(`YouTube API error: ${response.status}`);
    }

    const data = await response.json();
    
    if (!data.items || data.items.length === 0) {
      return [];
    }

    // Get detailed video information
    const videoIds = data.items.map(item => item.id.videoId).join(',');
    const videoDetails = await this.fetchVideoDetails(videoIds);

    return data.items.map((item, index) => ({
      id: item.id.videoId,
      title: item.snippet.title,
      description: item.snippet.description,
      thumbnail: item.snippet.thumbnails.high?.url || item.snippet.thumbnails.medium?.url,
      channelTitle: item.snippet.channelTitle,
      publishedAt: item.snippet.publishedAt,
      isLive: true,
      viewCount: videoDetails[index]?.statistics?.viewCount || '0',
      duration: videoDetails[index]?.contentDetails?.duration || 'PT0S'
    }));
  }

  // Fetch detailed video information
  async fetchVideoDetails(videoIds) {
    const params = new URLSearchParams({
      part: 'statistics,contentDetails',
      id: videoIds,
      key: this.apiKey
    });

    const response = await fetch(`${YOUTUBE_ENDPOINTS.VIDEO_DETAILS}?${params}`);
    
    if (!response.ok) {
      throw new Error(`YouTube API error: ${response.status}`);
    }

    const data = await response.json();
    return data.items || [];
  }

  // Mock data for when API key is not configured
  getMockLiveVideos() {
    return [
      {
        id: 'dQw4w9WgXcQ',
        title: 'Morning Aarti - Live from Temple',
        description: 'Join us for the beautiful morning aarti ceremony. Experience the divine energy and spiritual vibrations as we offer prayers to the deities.',
        thumbnail: 'https://img.youtube.com/vi/dQw4w9WgXcQ/maxresdefault.jpg',
        channelTitle: 'Spiritual Temple',
        publishedAt: new Date().toISOString(),
        isLive: true,
        viewCount: '1250',
        duration: 'PT1H30M'
      },
      {
        id: 'jNQXAC9IVRw',
        title: 'Evening Bhajan Session',
        description: 'Peaceful evening bhajans and devotional songs to calm your mind and connect with the divine.',
        thumbnail: 'https://img.youtube.com/vi/jNQXAC9IVRw/maxresdefault.jpg',
        channelTitle: 'Devotional Music',
        publishedAt: new Date().toISOString(),
        isLive: true,
        viewCount: '890',
        duration: 'PT2H15M'
      },
      {
        id: 'M7lc1UVf-VE',
        title: 'Spiritual Discourse - Live',
        description: 'Join our spiritual guru for an enlightening discourse on ancient wisdom and modern life applications.',
        thumbnail: 'https://img.youtube.com/vi/M7lc1UVf-VE/maxresdefault.jpg',
        channelTitle: 'Wisdom Talks',
        publishedAt: new Date().toISOString(),
        isLive: true,
        viewCount: '2100',
        duration: 'PT1H45M'
      }
    ];
  }

  // Get video embed URL
  getVideoEmbedUrl(videoId) {
    return `https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0`;
  }

  // Get video watch URL
  getVideoWatchUrl(videoId) {
    return `https://www.youtube.com/watch?v=${videoId}`;
  }
}

const youtubeService = new YouTubeService();
export default youtubeService;
